import {
  forwardRef,
  useId,
  useLayoutEffect,
  useRef,
  type HTMLAttributes,
  type ReactNode
} from "react";
import { cn } from "../../lib/cn";

export interface InlineConfirmProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  autoFocus?: boolean;
  restoreFocus?: boolean;
  title: ReactNode;
  description?: ReactNode;
  actions: ReactNode;
}

export const InlineConfirm = forwardRef<HTMLDivElement, InlineConfirmProps>(
  (
    {
      "aria-atomic": ariaAtomic = true,
      "aria-describedby": ariaDescribedBy,
      "aria-labelledby": ariaLabelledBy,
      "aria-live": ariaLive = "polite",
      autoFocus = true,
      title,
      description,
      actions,
      children,
      className,
      restoreFocus = true,
      role = "region",
      tabIndex = -1,
      ...props
    },
    ref
  ) => {
    const generatedID = useId();
    const titleID = `inline-confirm-${generatedID}-title`;
    const hasDescription = description !== undefined && description !== null && description !== false;
    const descriptionID = hasDescription ? `inline-confirm-${generatedID}-description` : undefined;
    const labelledBy = [titleID, ariaLabelledBy].filter(Boolean).join(" ");
    const describedBy = [descriptionID, ariaDescribedBy].filter(Boolean).join(" ") || undefined;
    const localRef = useRef<HTMLDivElement | null>(null);
    const returnFocusTarget = useRef<HTMLElement | null>(
      typeof document !== "undefined" && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
    );

    useLayoutEffect(() => {
      const returnFocusContainer = localRef.current?.parentElement;
      if (autoFocus) {
        const confirmation = localRef.current;
        confirmation?.focus({ preventScroll: true });
        confirmation?.scrollIntoView({
          behavior: "auto",
          block: "nearest",
          inline: "nearest"
        });
      }

      return () => {
        const target = returnFocusTarget.current;
        if (!restoreFocus) return;
        window.requestAnimationFrame(() => {
          const replacement = returnFocusContainer?.querySelector<HTMLElement>(
            "[data-inline-confirm-return], button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])"
          );
          const focusTarget = target?.isConnected && !target.hasAttribute("disabled")
            ? target
            : replacement;
          focusTarget?.focus({ preventScroll: true });
        });
      };
    }, [autoFocus, restoreFocus]);

    return (
      <div
        ref={(node) => {
          localRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        className={cn("ui-inline-confirm", className)}
        role={role}
        aria-live={ariaLive}
        aria-atomic={ariaAtomic}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        tabIndex={tabIndex}
        {...props}
      >
        <div className="ui-inline-confirm-copy">
          <strong id={titleID}>{title}</strong>
          {hasDescription && <p id={descriptionID}>{description}</p>}
          {children}
        </div>
        <InlineConfirmActions>{actions}</InlineConfirmActions>
      </div>
    );
  }
);

InlineConfirm.displayName = "InlineConfirm";

export function InlineConfirmActions({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("ui-inline-confirm-actions", className)} {...props} />;
}

export function DangerZone({ compact = false, className, ...props }: HTMLAttributes<HTMLDivElement> & { compact?: boolean }) {
  return <div className={cn("ui-danger-zone", compact && "ui-danger-zone-compact", className)} {...props} />;
}

export function DangerZoneCopy({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("ui-danger-zone-copy", className)} {...props} />;
}

export const DangerZoneConfirm = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("ui-danger-zone-confirm", className)} {...props} />
));

DangerZoneConfirm.displayName = "DangerZoneConfirm";
